import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DirectionalHint } from "@fluentui/react/lib/Callout";
import { Link } from "@fluentui/react/lib/Link";
import { Stack } from "@fluentui/react/lib/Stack";
import { Text } from "@fluentui/react/lib/Text";
import { TooltipHost, TooltipOverflowMode } from "@fluentui/react/lib/Tooltip";

import { EntityIcon } from "Components/Generic/EntityIcon";
import { IconButtonWithTooltip } from "Components/Generic/IconButtonWithTooltip";
import { IWithTranslatorProps, WithTranslator } from "Components/Generic/Translator";
import { EntityType } from "Model/EntityType";
import { IItem } from "Model/Storage/IItem";
import { uiSetActiveCompanyId, uiSetActiveContactId } from "Store/Actions";
import { selectActiveCompany, selectActiveContact, selectActiveProject } from "Store/Selectors/Projects";
import { getItemType, getName } from "Utils/Item";
import { isNotNullOrWhiteSpace, isNullOrWhiteSpace } from "Utils/String";
import { useHeaderTitleStyles } from "./UseHeaderTitleStyles";

export const HeaderTitle = WithTranslator((props: IWithTranslatorProps): JSX.Element => {
  const { translator } = props;
  const dispatch = useDispatch();
  const selectedProject = useSelector(selectActiveProject);
  const selectedCompany = useSelector(selectActiveCompany);
  const selectedContact = useSelector(selectActiveContact);

  const headerItem: IItem = selectedContact ?? selectedCompany ?? selectedProject;
  const headerText = getName(headerItem);
  const headerType = getItemType(headerItem);
  const isCompany = headerType === EntityType.Company;
  const isContact = headerType === EntityType.Contact;
  const { styles, iconStyles } = useHeaderTitleStyles(headerType);

  const targetUrl = headerItem?.[`@targetUrl`];

  const onCloseCompany = useCallback(() => {
    dispatch(uiSetActiveContactId(null));
    dispatch(uiSetActiveCompanyId(null));
  }, [dispatch]);

  const onCloseContact = useCallback(() => {
    dispatch(uiSetActiveContactId(null));
  }, [dispatch]);

  if (isNullOrWhiteSpace(headerText)) {
    return null;
  }

  return (
    <Stack horizontal className={styles.root} disableShrink>
      {(isCompany || isContact) && getBreadCrumb(getName(selectedProject), styles.breadCrumb, onCloseCompany)}

      {isContact && getBreadCrumb(getName(selectedCompany), styles.breadCrumb, onCloseContact)}

      <Stack.Item disableShrink>
        <EntityIcon type={headerType} styleSet={iconStyles} />
      </Stack.Item>

      <Stack.Item className={styles.title} grow>
        {getText(getName(headerItem))}
      </Stack.Item>

      {isNotNullOrWhiteSpace(targetUrl) && (
        <Stack.Item>
          <Link as="a" href={targetUrl} target="_blank">
            <IconButtonWithTooltip
              className={styles.targetLink}
              iconProps={{ iconName: `NavigateExternalInline` }}
              tooltip={translator.get(`tooltip.openexternal`, `Opens the exported record in an external window.`)}
            />
          </Link>
        </Stack.Item>
      )}
    </Stack>
  );
});

function getBreadCrumb(name: string, className: string, onClick: () => void): JSX.Element {
  return (
    <Stack horizontal className={className}>
      <Stack.Item grow onClick={onClick}>
        {getText(name)}
      </Stack.Item>
      <Stack.Item disableShrink>
        <Text variant="xLarge">/</Text>
      </Stack.Item>
    </Stack>
  );
}

function getText(name: string): JSX.Element {
  return (
    <Text block nowrap variant="xLarge">
      <TooltipHost
        content={name}
        directionalHint={DirectionalHint.bottomLeftEdge}
        overflowMode={TooltipOverflowMode.Parent}
      >
        {name}
      </TooltipHost>
    </Text>
  );
}
